import React, { useState } from 'react';
import HeaderTable from '../headerTable/headerTable';
import TabsTable from '../tabs/tabsTable';
import { COLUMN_CONFIGS } from '../../../functions/functions';

/**
 * Componente especializado para mostrar tablas de clasificación de motorsport
 * Genera dinámicamente tablas de pilotos y constructores basándose en los datos disponibles
 * Utiliza tabs para alternar entre diferentes tipos de clasificación
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.dataTab1 - Datos de clasificación de motorsport
 * @param {Object} props.dataTab1.motorsportStanding - Objeto con clasificaciones
 * @param {Object} props.dataTab1.motorsportStanding.peopleRankings - Clasificación de pilotos
 * @param {Object} props.dataTab1.motorsportStanding.teamRankings - Clasificación de equipos
 * @param {string} props.sport - Código del deporte para acceso dinámico a datos
 * 
 * @returns {JSX.Element} Componente con tabs de clasificaciones de motorsport
 */
export default function TableMotor({ dataTab1, sport }) {
  const [imageErrors, setImageErrors] = useState({});

  if (!dataTab1 || !sport) {
    return (
      <>
        <div className="positions-table__header">
          <HeaderTable title="Clasificaciones" />
        </div>
        <div className="positions-table__sport">
          <div className="no-data">No hay datos de clasificación disponibles</div>
        </div>
      </>
    );
  }
  
  const motorsportData = dataTab1[`${sport}Standing`] || {};
  const generateColumns = (type, sampleData) => {
    const config = COLUMN_CONFIGS[type];
    if (!config) return [];

    return config.fields
      .filter(field => sampleData.hasOwnProperty(field) && field !== 'lastName')
      .map(field => ({
        key: field,
        label: config.labels[field] || field,
        render: config.customRenders?.[field] || null
      }));
  };

  const createTableConfig = (id, name, data, dataKey, type) => {
    if (!data?.[dataKey]?.length) return null;
    const sampleData = data[dataKey][0];
    if (!sampleData) return null;
    
    return {
      id,
      name,
      data,
      dataKey,
      columns: generateColumns(type, sampleData)
    };
  };

  const generateTableConfig = () => {
    return [
      createTableConfig('drivers', 'Pilotos', motorsportData.peopleRankings, 'peopleRanking', 'drivers'),
      createTableConfig('teams', 'Constructores', motorsportData.teamRankings, 'teamRanking', 'teams')
    ].filter(Boolean);
  };

  const TableCell = ({ column, item, config, columnIndex }) => {
    if (!column.render) {
      const isConstructorsPoints = config?.id === 'teams' && columnIndex === 2;
      return (
        <td 
          className="team-item" 
          style={isConstructorsPoints ? { textAlign: 'right' } : {}}
        >
          {item[column.key] || '-'}
        </td>
      );
    }

    const result = column.render(item);
    
    // Renderizado para nombre de piloto (solo bandera + nombre)
    if (result?.name && result?.nationality) {
      const nationalityClass = result.nationality?.length === 3 
        ? `bandera-${result.nationality}` 
        : 'bandera-default';
      
      return (
        <td className="team-item">
          <div className="player-info">
            <div className={`bandera-sport ${nationalityClass}`}></div>
            <span className="driver-name">{result.name}</span>
          </div>
        </td>
      );
    }
    
    // Renderizado para imagen de escudería (columna separada)
    if (result?.teamImage) {
      const imageKey = `escuderia-${item.id || item.position}`;
      const hasImageError = imageErrors[imageKey];
      
      return (
        <td className="team-item">
          <div className={`team-image-container${hasImageError ? '-default' : ''}`}>
            {!hasImageError && (
              <img 
                src={result.teamImage} 
                alt="escuderia" 
                loading="lazy" 
                className="escuderia-f1"
                onError={() => {
                  setImageErrors(prev => ({ ...prev, [imageKey]: true }));
                }}
              />
            )}
          </div>
        </td>
      );
    }
    
    // Renderizado para equipos (con teamName e image)
    if (result?.teamName) {
      const imageKey = `team-${item.id || item.position}`;
      const hasImageError = imageErrors[imageKey];
      
      return (
        <td className="team-item">
          <div className={`team-info team-info${hasImageError ? '-default' : ''}`}>
            {result.image && !hasImageError && (
              <img 
                src={result.image} 
                alt={result.teamName} 
                loading="lazy" 
                className="team-logo"
                onError={() => {
                  setImageErrors(prev => ({ ...prev, [imageKey]: true }));
                }}
              />
            )}
            {hasImageError && (
              <div className="team-logo-default" />
            )}
            <span className="team-name">{result.teamName}</span>
          </div>
        </td>
      );
    }
    
    return <td className="team-item">{result}</td>;
  };

  /**
   * Componente reutilizable para renderizar tabla completa
   * Genera estructura HTML de tabla con datos dinámicos
   */
  const MotorsportTable = ({ config, title }) => {
    const data = config.data[config.dataKey];
    
    if (!data || data.length === 0) {
      return (
        <div className="table-content">
          <div className="no-data">No hay datos disponibles</div>
        </div>
      );
    }

    return (
      <div className="table-content">
        <div className="conference-section marginB10">
          <table className="positions-table table-f1">
            <thead>
              <tr>
                {config.columns.map((column, index) => {
                  const isConstructorsPoints = config.id === 'teams' && index === 2;
                  return (
                    <th 
                      key={column.key} 
                      style={isConstructorsPoints ? { textAlign: 'right' } : {}}
                    >
                      {column.label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={item?.id || item?.position || `${config.id}-${index}`} className={index < 3 ? 'f1-podium' : ''}>
                  {config.columns.map((column, columnIndex) => (
                    <TableCell 
                      key={column?.key || `col-${index}`} 
                      column={column} 
                      item={item || {}} 
                      config={config}
                      columnIndex={columnIndex}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const tableConfigs = generateTableConfig();
  const stages = tableConfigs.map((config, index) => ({ 
    id: config?.id || `stage-${index}`,
    name: config?.name || `Stage ${index + 1}`,
    config 
  }));

  const renderTable = (stage) => <MotorsportTable config={stage.config} title={stage.name} />;

  return (
    <>
      <div className="positions-table__header">
        <HeaderTable title="Clasificaciones" />
      </div>
      <div className="positions-table__sport">
        {stages.length > 0 ? (
          <TabsTable stages={stages} renderTable={renderTable} />
        ) : (
          <div className="no-data">No hay datos de clasificación disponibles</div>
        )}
      </div>
    </>
  );
}